import {
  getLastLoteNegotiationWithParts,
  mapItensPartsToVehicleProposals,
  mapProposalStatus,
} from './loteProposalUtils'

export const getEditVehiclePath = (vehicleGroup) => {
  if (!vehicleGroup || vehicleGroup === 'NOT_FOUND') return null

  if (vehicleGroup.fleet === false) {
    return `/vehicle/${vehicleGroup.id}/third/edit`
  }

  return `/vehicle/${vehicleGroup.fleetId}/own/edit`
}

export const getLastTransporterPercentMargin = (negotiations) => {
  if (!negotiations?.length) return null

  for (let index = negotiations.length - 1; index >= 0; index -= 1) {
    const item = negotiations[index]
    if (item.agent === 'TRANSPORTER' && item.percentMargin != null) {
      return Number(item.percentMargin)
    }
  }

  return null
}

const mapItensPartsToQuantitiesByVehicleId = (itensParts) => {
  const quantitiesByVehicleId = {}

  ;(itensParts || []).forEach((part) => {
    if (mapProposalStatus(part.status) === 'refused') return

    const vehicleTypeId = part.negotiationConfig?.vehicleTypeAntt?.id
    if (vehicleTypeId == null || part.availableQuantity == null) return

    quantitiesByVehicleId[vehicleTypeId] = part.availableQuantity
  })

  return quantitiesByVehicleId
}

const mapItensPartsToValuesByVehicleId = (itensParts) => {
  const valuesByVehicleId = {}

  ;(itensParts || []).forEach((part) => {
    if (mapProposalStatus(part.status) === 'refused') return

    const vehicleTypeId = part.negotiationConfig?.vehicleTypeAntt?.id
    if (vehicleTypeId == null || part.value == null) return

    valuesByVehicleId[vehicleTypeId] = part.value
  })

  return valuesByVehicleId
}

const mapItensPartsToPercentMarginsByVehicleId = (itensParts, itemPercentMargin) => {
  const percentMarginsByVehicleId = {}

  ;(itensParts || []).forEach((part) => {
    if (mapProposalStatus(part.status) === 'refused') return

    const vehicleTypeId = part.negotiationConfig?.vehicleTypeAntt?.id
    if (vehicleTypeId == null) return

    const percentMargin = getPartPercentMargin(part, itemPercentMargin)
    if (percentMargin != null) {
      percentMarginsByVehicleId[vehicleTypeId] = percentMargin
    }
  })

  return percentMarginsByVehicleId
}

const isLockedPartStatus = (status) => status === 'accepted' || status === 'refused'

const hasPartId = (part) => part?.id != null

const getRawProposalParts = (item) => {
  if (!item) return []

  const fromParts = item.itensParts ?? []
  const fromPart = item.itensPart ?? []

  if (!fromParts.length) return fromPart
  if (!fromPart.length || fromParts === fromPart) return fromParts
  return [...fromParts, ...fromPart]
}

const uniquePartsById = (parts) => {
  const seenIds = new Set()
  return parts.filter((part) => {
    if (!hasPartId(part)) return true
    const partId = String(part.id)
    if (seenIds.has(partId)) return false
    seenIds.add(partId)
    return true
  })
}

const dedupeNegotiationItemParts = (parts) => {
  const seenIds = new Set()
  const seenConfigKeys = new Set()
  const uniqueParts = []

  ;(parts || []).forEach((part) => {
    if (hasPartId(part)) {
      if (seenIds.has(String(part.id))) return
      seenIds.add(String(part.id))
    }

    const configKey = part.negotiationConfig?.id ?? part.negotiationConfig?.vehicleTypeAntt?.id ?? null
    if (configKey != null && seenConfigKeys.has(configKey)) return

    if (configKey != null) seenConfigKeys.add(configKey)
    uniqueParts.push(part)
  })

  return uniqueParts
}

export const parseNegotiationDate = (value) => {
  if (!value) return 0

  const [datePart, timePart = '00:00:00'] = String(value).split(' ')
  const [day, month, year] = datePart.split('/')
  if (year && month && day) {
    return new Date(`${year}-${month}-${day}T${timePart}`).getTime() || 0
  }

  return Date.parse(value) || 0
}

export const getPartPercentMargin = (part, itemPercentMargin) => {
  if (part.percentMargin != null) return part.percentMargin
  if (part.percentage != null) return part.percentage
  if (itemPercentMargin != null) return itemPercentMargin
  return undefined
}

const partWithLockedSnapshot = (locked) => ({
  ...locked.part,
  ...(locked.percentMargin != null ? { percentMargin: locked.percentMargin } : {}),
})

const getScanItems = (negotiations) =>
  (negotiations || []).filter((item) => !item.firstPurpose)

const collectLockedParts = (negotiations, negotiationParts) => {
  const decidedStatusByPartId = new Map()

  ;(negotiationParts || []).forEach((negotiationPart) => {
    const status = mapProposalStatus(negotiationPart.status)
    if (!isLockedPartStatus(status)) return
    if (negotiationPart.negotiationItemPartId == null) return

    decidedStatusByPartId.set(String(negotiationPart.negotiationItemPartId), status)
  })

  const latestOccurrenceById = new Map()
  const firstSeenTimeById = new Map()
  const rawStatusByPartId = new Map()

  getScanItems(negotiations).forEach((item) => {
    const itemTime = parseNegotiationDate(item.date)

    getRawProposalParts(item).forEach((part) => {
      if (!hasPartId(part)) return

      const partId = String(part.id)

      const latestOccurrence = latestOccurrenceById.get(partId)
      if (!latestOccurrence || itemTime >= latestOccurrence.itemTime) {
        latestOccurrenceById.set(partId, {
          part,
          itemTime,
          percentMargin: getPartPercentMargin(part, item.percentMargin),
        })
      }

      const firstSeenTime = firstSeenTimeById.get(partId)
      if (firstSeenTime === undefined || itemTime < firstSeenTime) {
        firstSeenTimeById.set(partId, itemTime)
      }

      const rawStatus = mapProposalStatus(part.status)
      if (isLockedPartStatus(rawStatus)) rawStatusByPartId.set(partId, rawStatus)
    })
  })

  const lockedParts = []

  latestOccurrenceById.forEach((latestOccurrence, partId) => {
    const status = decidedStatusByPartId.get(partId) ?? rawStatusByPartId.get(partId)
    if (!status) return

    lockedParts.push({
      part: { ...latestOccurrence.part, status },
      // A terminal status has no per-bubble history: it becomes visible on the first bubble
      // that already contained the part, so every later bubble shows the current status.
      visibleFromItemTime: firstSeenTimeById.get(partId) ?? latestOccurrence.itemTime,
      percentMargin: latestOccurrence.percentMargin,
    })
  })

  return lockedParts
}

const getPartVehicleTypeId = (part) => part?.negotiationConfig?.vehicleTypeAntt?.id

const getCanonicalParts = (negotiations, lockedPartIds) => {
  let canonical = []
  let bestScore = -1

  getScanItems(negotiations).forEach((item) => {
    const parts = getRawProposalParts(item)
    if (!parts.length) return

    const lockedHits = parts.reduce(
      (count, part) => (hasPartId(part) && lockedPartIds.has(String(part.id)) ? count + 1 : count),
      0,
    )
    const score = parts.length * 100 + lockedHits
    if (score <= bestScore) return

    bestScore = score
    canonical = parts
  })

  return canonical
}

const indexParts = (parts) => {
  const byId = new Map()
  const byVehicleTypeId = new Map()

  parts.forEach((part) => {
    if (hasPartId(part)) byId.set(String(part.id), part)
    const vehicleTypeId = getPartVehicleTypeId(part)
    if (vehicleTypeId != null && !byVehicleTypeId.has(vehicleTypeId)) {
      byVehicleTypeId.set(vehicleTypeId, part)
    }
  })

  return { byId, byVehicleTypeId }
}

const mergePartsInCanonicalOrder = (currentParts, lockedParts, canonicalParts) => {
  const current = indexParts(currentParts)
  const locked = indexParts(lockedParts)
  const merged = []
  const usedIds = new Set()

  const pushPart = (part) => {
    if (!part) return
    const partId = hasPartId(part) ? String(part.id) : null
    if (partId && usedIds.has(partId)) return
    if (partId) usedIds.add(partId)

    const lockedPart = partId ? locked.byId.get(partId) : null
    const lockedStatus = lockedPart ? mapProposalStatus(lockedPart.status) : null
    if (lockedPart && isLockedPartStatus(lockedStatus)) {
      merged.push(lockedPart)
      return
    }

    merged.push(part)
  }

  canonicalParts.forEach((canonicalPart) => {
    const partId = hasPartId(canonicalPart) ? String(canonicalPart.id) : null
    const vehicleTypeId = getPartVehicleTypeId(canonicalPart)

    pushPart(
      (partId && current.byId.get(partId))
      || (vehicleTypeId != null && current.byVehicleTypeId.get(vehicleTypeId))
      || (partId && locked.byId.get(partId))
      || (vehicleTypeId != null && locked.byVehicleTypeId.get(vehicleTypeId))
      || null,
    )
  })

  currentParts.forEach(pushPart)
  lockedParts.forEach(pushPart)

  return merged
}

const mergeLockedPartsIntoItem = (item, scanItems, lockedParts) => {
  const ownParts = item.agent === 'SHIPPER'
    ? dedupeNegotiationItemParts(getRawProposalParts(item))
    : uniquePartsById(getRawProposalParts(item))

  const itemTime = parseNegotiationDate(item.date)
  const lockedFromHere = lockedParts
    .filter((locked) => itemTime >= locked.visibleFromItemTime)
    .map(partWithLockedSnapshot)

  if (!lockedFromHere.length) {
    return { ...item, itensParts: ownParts }
  }

  const lockedPartIds = new Set(lockedFromHere.map((part) => String(part.id)))
  const itensParts = mergePartsInCanonicalOrder(
    ownParts,
    lockedFromHere,
    getCanonicalParts(scanItems, lockedPartIds),
  )

  return { ...item, itensParts }
}

export const pickLatestTransporterNegotiation = (negotiations) => {
  const transporters = getScanItems(negotiations).filter(
    (item) => item.agent === 'TRANSPORTER' && getRawProposalParts(item).length,
  )

  if (!transporters.length) return null

  return transporters.reduce(
    (latest, item) =>
      (!latest || parseNegotiationDate(item.date) >= parseNegotiationDate(latest.date) ? item : latest),
    null,
  )
}

export const collectTransporterPartsForDisplay = (negotiations, negotiationParts) => {
  const latestItem = pickLatestTransporterNegotiation(negotiations)
  if (!latestItem) return []

  const scanItems = getScanItems(negotiations)
  const lockedParts = collectLockedParts(scanItems, negotiationParts)
  const enrichedLatest = mergeLockedPartsIntoItem(latestItem, scanItems, lockedParts)

  return uniquePartsById(getRawProposalParts(enrichedLatest))
}

export const getEnrichedLatestTransporterNegotiation = (negotiations, negotiationParts) => {
  const latestItem = pickLatestTransporterNegotiation(negotiations)
  if (!latestItem) return null

  const scanItems = getScanItems(negotiations)
  const lockedParts = collectLockedParts(scanItems, negotiationParts)
  return mergeLockedPartsIntoItem(latestItem, scanItems, lockedParts)
}

const getLastTransporterLoteNegotiation = (negotiations, negotiationParts) => {
  if (negotiationParts?.length) {
    return getEnrichedLatestTransporterNegotiation(negotiations, negotiationParts)
  }

  const negotiation = getLastLoteNegotiationWithParts(negotiations)
  if (!negotiation || negotiation.agent !== 'TRANSPORTER') return null
  return negotiation
}

export const getLastLoteQuantitiesByVehicleId = (negotiations, negotiationParts) => {
  const negotiation = getLastTransporterLoteNegotiation(negotiations, negotiationParts)
  if (!negotiation) return {}
  return mapItensPartsToQuantitiesByVehicleId(negotiation.itensParts)
}

export const getLastLoteValuesByVehicleId = (negotiations, negotiationParts) => {
  const negotiation = getLastTransporterLoteNegotiation(negotiations, negotiationParts)
  if (!negotiation) return {}
  return mapItensPartsToValuesByVehicleId(negotiation.itensParts)
}

export const getLastLotePercentMarginsByVehicleId = (negotiations, negotiationParts) => {
  const negotiation = getLastTransporterLoteNegotiation(negotiations, negotiationParts)
  if (!negotiation) return {}
  return mapItensPartsToPercentMarginsByVehicleId(
    negotiation.itensParts,
    negotiation.percentMargin,
  )
}

export const getLastLotePercentMargin = (negotiations) => {
  const negotiation = getLastTransporterLoteNegotiation(negotiations)
  if (negotiation?.percentMargin != null) {
    return Number(negotiation.percentMargin)
  }

  return getLastTransporterPercentMargin(negotiations)
}

export const getLockedLotePartsByVehicleId = (negotiations, negotiationParts) => {
  const lockedParts = collectLockedParts(getScanItems(negotiations), negotiationParts).map(partWithLockedSnapshot)
  const enrichedLatest = getEnrichedLatestTransporterNegotiation(negotiations, negotiationParts)
  const lastPartByVehicleTypeId = new Map()

  getRawProposalParts(enrichedLatest).forEach((part) => {
    const vehicleTypeId = getPartVehicleTypeId(part)
    if (vehicleTypeId == null || lastPartByVehicleTypeId.has(vehicleTypeId)) return
    lastPartByVehicleTypeId.set(vehicleTypeId, part)
  })

  const lockedByVehicleId = {}

  lockedParts.forEach((part) => {
    const vehicleTypeId = getPartVehicleTypeId(part)
    if (vehicleTypeId == null) return

    const lastPartOfType = lastPartByVehicleTypeId.get(vehicleTypeId)
    if (lastPartOfType && String(lastPartOfType.id) !== String(part.id)) {
      const lastStatus = mapProposalStatus(lastPartOfType.status)
      if (!isLockedPartStatus(lastStatus)) return
    }

    lockedByVehicleId[vehicleTypeId] = {
      status: part.status,
      availableQuantity: part.availableQuantity,
      value: part.value,
      icmsPrice: part.icmsPrice ?? part.negotiationConfig?.icmsPrice,
      marginValue: part.marginValue,
      freightPriceMin: part.negotiationConfig?.freightPriceMin,
      tollPrice: part.negotiationConfig?.tollPrice,
      percentMargin: getPartPercentMargin(part),
      reason: typeof part.reason === 'string' ? part.reason.trim() : undefined,
    }
  })

  return lockedByVehicleId
}

export const enrichNegotiationItemWithLockedParts = (
  item,
  negotiations,
  negotiationParts,
) => {
  if (!item || item.firstPurpose) return item

  if (item.agent === 'SHIPPER') {
    const currentParts = dedupeNegotiationItemParts(getRawProposalParts(item))
    return { ...item, itensParts: currentParts }
  }

  const scanItems = getScanItems(negotiations)
  const lockedParts = collectLockedParts(scanItems, negotiationParts)
  return mergeLockedPartsIntoItem(item, scanItems, lockedParts)
}

/** Display-only enrichment: shows locked parts on every bubble, including SHIPPER ones. */
export const buildLoteBubbleItem = (item, negotiations, negotiationParts) => {
  if (!item || item.firstPurpose) return item

  const scanItems = getScanItems(negotiations)
  const lockedParts = collectLockedParts(scanItems, negotiationParts)
  return mergeLockedPartsIntoItem(item, scanItems, lockedParts)
}

export const buildReadonlyProposalData = (item, load) => {
  const enrichedItem = buildLoteBubbleItem(
    item,
    load?.negotiations,
    load?.negotiationParts,
  )
  const itemConfig = enrichedItem?.negotiationConfig
  const vehicleProposals = enrichedItem.itensParts?.length
    ? mapItensPartsToVehicleProposals(enrichedItem)
    : enrichedItem.vehicleProposals || null

  return {
    ...enrichedItem,
    estimatedDate: (load && load.estimatedDate) || enrichedItem.estimatedDate,
    vehicleTypeAntt: enrichedItem.vehicleTypeAntt || itemConfig?.vehicleTypeAntt,
    vehicleProposals,
  }
}

export const buildAcceptedLoteProposalPayload = ({
  shipperItem,
  negotiateByMargin,
  estimatedDate,
}) => {
  const firstPart = shipperItem.itensParts?.[0]
  const urlRoute = shipperItem.urlRoute
    || firstPart?.negotiationConfig?.urlRoute
    || ''

  const parts = (shipperItem.itensParts ?? [])
    .filter((part) => !isLockedPartStatus(mapProposalStatus(part.status)))
    .map((part) => {
      const entry = {
        shippingNegotiationConfigId: part.negotiationConfig?.id,
        availableQuantity: part.availableQuantity,
      }

      if (!negotiateByMargin) {
        entry.value = part.value
      }

      return entry
    })

  const makeProposalLotByVehicleType = {
    parts,
    typeTransport: shipperItem.typeTransport || 'ETC',
    urlRoute,
  }

  if (negotiateByMargin) {
    makeProposalLotByVehicleType.percentMargin = Number(shipperItem.percentMargin) || 0
  }

  return {
    estimatedDate,
    observation: shipperItem.observation || '',
    makeProposalLotByVehicleType,
  }
}

export const buildAcceptedProposalPayload = ({
  shipperItem,
  negotiationConfig,
  negotiateByMargin,
  estimatedDate,
  isLote = false,
}) => {
  if (isLote && shipperItem.itensParts?.length) {
    return buildAcceptedLoteProposalPayload({
      shipperItem,
      negotiateByMargin,
      estimatedDate,
    })
  }

  const makeProposalByVehicleType = {
    shippingNegotiationConfigId: negotiationConfig?.id,
    typeTransport: shipperItem.typeTransport || 'ETC',
    urlRoute: shipperItem.urlRoute || negotiationConfig?.urlRoute,
  }

  if (negotiateByMargin) {
    return {
      estimatedDate,
      observation: shipperItem.observation || '',
      makeProposalByVehicleType: {
        ...makeProposalByVehicleType,
        percentMargin: Number(shipperItem.percentMargin) || 0,
      },
    }
  }

  return {
    estimatedDate,
    observation: shipperItem.observation || '',
    value: shipperItem.value,
    makeProposalByVehicleType,
  }
}

const SECONDARY_VEHICLE_KEY = /^secondaryVehicle(\d+)$/

export const isVehicleNotFoundError = (error) =>
  error && (error.status === 404 || error.status === 422)

export const getUnitTypeForPlateIndex = (index) => (index === 0 ? 'vehicle' : 'trailer')

export const buildPreviewVehicleGroup = (vehiclesByIndex, plateAmount) => {
  const slotCount = plateAmount || Object.keys(vehiclesByIndex).length
  const group = {}
  let hasAny = false

  for (let index = 0; index < slotCount; index += 1) {
    const vehicle = vehiclesByIndex[index]
    if (vehicle) {
      hasAny = true
      if (index === 0) {
        group.primaryVehicle = vehicle
        group.plate = vehicle.plate
      } else {
        group[`secondaryVehicle${index}`] = vehicle
      }
    }
  }

  return hasAny ? group : null
}

export const getVehiclesByPlateIndex = (vehicleGroup, plateAmount) => {
  if (!vehicleGroup || vehicleGroup === 'NOT_FOUND' || !plateAmount) return []

  return Array.from({ length: plateAmount }, (_, index) => {
    if (index === 0) return vehicleGroup.primaryVehicle || null
    return vehicleGroup[`secondaryVehicle${index}`] || null
  })
}

export const getVehiclesFromGroup = (vehicleGroup) => {
  if (!vehicleGroup || vehicleGroup === 'NOT_FOUND') return []

  const vehicles = []

  if (vehicleGroup.primaryVehicle) {
    vehicles.push(vehicleGroup.primaryVehicle)
  }

  Object.keys(vehicleGroup)
    .filter((key) => SECONDARY_VEHICLE_KEY.test(key) && vehicleGroup[key])
    .sort((a, b) => {
      const indexA = Number(a.match(SECONDARY_VEHICLE_KEY)[1])
      const indexB = Number(b.match(SECONDARY_VEHICLE_KEY)[1])
      return indexA - indexB
    })
    .forEach((key) => {
      vehicles.push(vehicleGroup[key])
    })

  return vehicles
}
